var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _ReauthenticatableClient_pendingRefresh, _ReauthenticatableClient_signal;
import { TokenError, TokenExpiredError } from "../errors.js";
import { FailedAfterRetryError, throwError } from "./errors.js";
import { RefreshTokenExpiredError, UserMissingRefreshTokenError, } from "../errors.js";
const DEFAULT_RETRY_COUNT = 2;
/**
 * Client that performs a fetch and is able to refresh the accessToken
 * when it's expired.
 *
 * Key design decisions:
 *  - Token refresh is deduplicated: if multiple requests detect an expired
 *    token at the same time, only ONE refresh call is made and all waiters
 *    share the result.
 *  - Retries use a bounded iterative loop (no recursion) to avoid
 *    "max call stack" / "max recursion depth" errors.
 *  - The AbortController is scoped per-request rather than shared, so
 *    unrelated requests don't interfere with each other.
 */
export class ReauthenticatableClient {
    constructor(auth) {
        /**
         * Holds the in-flight refresh promise so that concurrent callers
         * can coalesce onto a single network request.
         * `null` when no refresh is in progress.
         */
        _ReauthenticatableClient_pendingRefresh.set(this, null);
        _ReauthenticatableClient_signal.set(this, undefined);
        __classPrivateFieldSet(this, _ReauthenticatableClient_signal, () => AbortSignal.timeout(10000), "f");
        this.auth = auth;
    }
    /**
     * Thin wrapper around `window.fetch` that throws on non-ok responses.
     */
    doFetch(...args) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield fetch(args === null || args === void 0 ? void 0 : args[0], Object.assign({ signal: __classPrivateFieldGet(this, _ReauthenticatableClient_signal, "f") ? __classPrivateFieldGet(this, _ReauthenticatableClient_signal, "f").call(this) : undefined }, args === null || args === void 0 ? void 0 : args[1]));
            if (!res.ok) {
                yield throwError(res);
            }
            return res;
        });
    }
    /**
     * Build a `Headers` object that includes the current access token
     * in the `Authorization` header (if one exists).
     */
    createHeadersWithAuth(headersInit) {
        var _a;
        const headers = new Headers(headersInit);
        const session = (_a = this.auth.currentUser) === null || _a === void 0 ? void 0 : _a.session;
        if (session) {
            headers.set("Authorization", `Bearer ${session.token}`);
        }
        return headers;
    }
    /**
     * Merge caller-provided `RequestInit` with auth headers (when requested).
     */
    buildRequestInit(init) {
        const merged = Object.assign({}, init);
        merged.headers = this.createHeadersWithAuth(init === null || init === void 0 ? void 0 : init.headers);
        return merged;
    }
    /**
     * Ensures that only one refresh request is in-flight at a time.
     *
     * If a refresh is already running, callers await the same promise
     * instead of firing a second network request.
     */
    ensureTokenRefreshed(refreshPath) {
        return __awaiter(this, void 0, void 0, function* () {
            if (__classPrivateFieldGet(this, _ReauthenticatableClient_pendingRefresh, "f")) {
                // Another call is already refreshing – just wait for it.
                return __classPrivateFieldGet(this, _ReauthenticatableClient_pendingRefresh, "f");
            }
            __classPrivateFieldSet(this, _ReauthenticatableClient_pendingRefresh, this.doPerformTokenRefresh(refreshPath).finally(() => {
                // Clear the slot regardless of success/failure so that future
                // callers can attempt a fresh refresh.
                __classPrivateFieldSet(this, _ReauthenticatableClient_pendingRefresh, null, "f");
            }), "f");
            return __classPrivateFieldGet(this, _ReauthenticatableClient_pendingRefresh, "f");
        });
    }
    /**
     * Actually performs the token refresh against the identity service.
     * This should only be called via `ensureTokenRefreshed`.
     */
    doPerformTokenRefresh(refreshPath) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.auth.refreshSession();
        });
    }
    fetch(input, init, opts_) {
        return __awaiter(this, void 0, void 0, function* () {
            const opts = opts_ !== null && opts_ !== void 0 ? opts_ : {
                retryCount: DEFAULT_RETRY_COUNT,
            };
            const maxAttempts = Math.max(1, opts.retryCount + 1); // at least 1 attempt
            let lastError;
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                // ---- Pre-flight: make sure we have an access token ----------- //
                const currentUser = this.auth.currentUser;
                if (!(currentUser === null || currentUser === void 0 ? void 0 : currentUser.session) && !(currentUser === null || currentUser === void 0 ? void 0 : currentUser.refreshToken)) {
                    this.auth.removeAuthState();
                    throw new UserMissingRefreshTokenError();
                }
                // We have a refresh token but no session – refresh first.
                if (!(currentUser === null || currentUser === void 0 ? void 0 : currentUser.session) && (currentUser === null || currentUser === void 0 ? void 0 : currentUser.refreshToken)) {
                    try {
                        yield this.ensureTokenRefreshed(currentUser.refreshTokenPath);
                    }
                    catch (err) {
                        if (err instanceof RefreshTokenExpiredError ||
                            err instanceof UserMissingRefreshTokenError) {
                            yield this.auth.removeAuthState();
                            throw err;
                        }
                        // For other refresh errors, record and retry on next iteration.
                        lastError = err;
                        continue;
                    }
                }
                // ---- Perform the actual request ------------------------------ //
                try {
                    return yield this.doFetch(input, this.buildRequestInit(init));
                }
                catch (err) {
                    lastError = err;
                    // Token expired → try refreshing and loop back.
                    if (err instanceof TokenExpiredError || err instanceof TokenError) {
                        console.error("Token expired. attempting to refetch ", err);
                        try {
                            yield this.ensureTokenRefreshed(currentUser.refreshTokenPath);
                        }
                        catch (refreshErr) {
                            if (refreshErr instanceof RefreshTokenExpiredError ||
                                refreshErr instanceof UserMissingRefreshTokenError) {
                                this.auth.removeAuthState();
                                throw refreshErr;
                            }
                            // Surface the refresh error as the last error and keep looping.
                            lastError = refreshErr;
                        }
                        // Continue to next attempt with (hopefully) a fresh token.
                        continue;
                    }
                    // For any other error (network failure, 5xx mapped to WebError, etc.)
                    // bubble immediately – retrying won't help.
                    throw err;
                }
            }
            // All attempts exhausted.
            throw new FailedAfterRetryError(lastError);
        });
    }
}
_ReauthenticatableClient_pendingRefresh = new WeakMap(), _ReauthenticatableClient_signal = new WeakMap();
